/**
 * Subscription figures from `skidaCrmSubscriptions` — the RevenueCat-fed copy.
 *
 * This is the accurate source. The `users.activeEntitlements` mirror only
 * refreshes when the app syncs, so it misses renewals from customers who have
 * not opened Skida since: it reported 1,610 live subscriptions where RevenueCat
 * knows of 1,938. A 200-account sample showed every single discrepancy was a
 * yearly renewal dated exactly one year later than the mirror believed.
 *
 * Sandbox rows are excluded from every figure — test purchases must never
 * inflate a production number.
 */
import {
  collection,
  getCountFromServer,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from 'firebase/firestore'
import { db } from '@/shared/firebase/client'
import { CRM_COLLECTIONS } from '@/shared/firebase/collections'

/**
 * Subscriptions, not subscribers.
 *
 * One account can hold several subscriptions — 200 do — and the business counts
 * subscriptions, so every figure below counts subscriptions. The matching
 * per-person numbers live in `SubscriberStats`; keeping them in separate types
 * is what stops the two from being mixed in one calculation by accident.
 */
export interface SubscriptionStats {
  /** Subscriptions on file, live or expired. */
  total: number
  /** Subscriptions whose access has not expired. */
  live: number
  /** Of those, set to auto-renew. */
  renewing: number
  /** Of those, cancelled but still paid up. */
  notRenewing: number
  /** Live and on a trial period. */
  trialing: number
  /** Expired subscriptions. */
  lapsed: number
}

/**
 * The same population counted per *person*, from the `skidaCrmSubscribers`
 * rollup. Needed because 200 accounts hold more than one subscription: a plan
 * mix built from subscription counts would overstate the paying share by that
 * much. See src/shared/util/subscriberRollup.ts for who writes these fields.
 */
export interface SubscriberStats {
  /** People who have ever subscribed. */
  total: number
  /** People whose access has not expired, on a paid period. */
  live: number
  /** People whose only live subscriptions are trials. */
  trialing: number
  /** People who once subscribed and no longer have access. */
  lapsed: number
}

export interface FeedStatus {
  /** Total events stored. Zero until the webhook sees its first lifecycle event. */
  events: number
  /** Most recent events, newest first. */
  recent: { type: string; atMs: number; sandbox: boolean }[]
  /** Documents that came from the one-off backfill rather than a live event. */
  backfilled: number
}

const subsRef = () => collection(db, CRM_COLLECTIONS.subscriptions)

async function count(...clauses: Parameters<typeof where>[]): Promise<number> {
  // Every query excludes sandbox rows.
  const q = query(subsRef(), where('sandbox', '==', false), ...clauses.map((c) => where(...c)))
  const snap = await getCountFromServer(q)
  return snap.data().count
}

export async function loadSubscriptionStats(now: number = Date.now()): Promise<SubscriptionStats> {
  const [total, live, renewing, notRenewing, trialing, lapsed] = await Promise.all([
    count(),
    count(['expiresAtMs', '>', now]),
    count(['willRenew', '==', true], ['expiresAtMs', '>', now]),
    count(['willRenew', '==', false], ['expiresAtMs', '>', now]),
    count(['periodType', '==', 'TRIAL'], ['expiresAtMs', '>', now]),
    count(['expiresAtMs', '<=', now]),
  ])

  return { total, live, renewing, notRenewing, trialing, lapsed }
}

const subscribersRef = () => collection(db, CRM_COLLECTIONS.subscribers)

async function countPeople(...clauses: Parameters<typeof where>[]): Promise<number> {
  const q = query(subscribersRef(), where('sandbox', '==', false), ...clauses.map((c) => where(...c)))
  const snap = await getCountFromServer(q)
  return snap.data().count
}

/**
 * Per-person subscription state.
 *
 * `status` is denormalised rather than derived here: deriving it would mean
 * reading every subscription document into the browser, which is the one thing
 * this collection exists to avoid.
 */
export async function loadSubscriberStats(): Promise<SubscriberStats> {
  const [total, live, trialing, lapsed] = await Promise.all([
    countPeople(),
    countPeople(['status', '==', 'live']),
    countPeople(['status', '==', 'trialing']),
    countPeople(['status', '==', 'lapsed']),
  ])
  return { total, live, trialing, lapsed }
}

export async function loadFeedStatus(): Promise<FeedStatus> {
  const [evts, backfilled] = await Promise.all([
    getCountFromServer(query(collection(db, CRM_COLLECTIONS.subscriptionEvents))),
    getCountFromServer(query(subsRef(), where('source', '==', 'backfill'))),
  ])

  let recent: FeedStatus['recent'] = []
  if (evts.data().count > 0) {
    const snap = await getDocs(
      query(
        collection(db, CRM_COLLECTIONS.subscriptionEvents),
        orderBy('eventAtMs', 'desc'),
        limit(5),
      ),
    )
    recent = snap.docs.map((d) => ({
      type: d.data().type ?? 'UNKNOWN',
      atMs: typeof d.data().eventAtMs === 'number' ? d.data().eventAtMs : 0,
      sandbox: d.data().sandbox === true,
    }))
  }

  return { events: evts.data().count, recent, backfilled: backfilled.data().count }
}
